import { Heading, VStack, SimpleGrid, SimpleGridProps } from '@chakra-ui/react';
import { ScoreBadge } from 'shared-components';
import { IRecordedInterviewAverageScores, IRecordedInterviewEntry } from 'api-paths-cas';
import { IRecordedInterviewAverageScoresExtended } from '../../types/index';
import { getFacialRecognitionScore } from '../../utilities/scheduledInterviews';

interface IProps {
  interview: IRecordedInterviewEntry;
  size?: number;
  headingSize?: string;
  gridProps?: SimpleGridProps;
}

type TAverageScoresListItem = {
  key: 'facialRecognitionScore' | 'isReadingFromScript' | 'isLipSyncing' | keyof IRecordedInterviewAverageScores;
  label: string;
};
const AVERAGE_SCORES_LIST: Array<TAverageScoresListItem> = [
  { key: 'facialRecognitionScore', label: 'Identity' },
  { key: 'isReadingFromScript', label: 'Reading' },
  { key: 'isLipSyncing', label: 'Lip sync' },
  { key: 'averageTranscriptScore', label: 'Audio' },
  { key: 'averageLanguageProficiencyScore', label: 'Language' },
  { key: 'averageAnswerScore', label: 'Score' },
];

export default function RecordedInterviewAverageScoresSection({
  interview,
  size = 12,
  headingSize = '16px',
  gridProps,
}: IProps) {
  const { scoresAverage, facialRecognitionScore, userAssessment } = interview;
  const averageScores = Object.assign(scoresAverage ?? {}, {
    facialRecognitionScore: getFacialRecognitionScore(facialRecognitionScore),
    isReadingFromScript: userAssessment?.isReadingFromScript,
    isLipSyncing: userAssessment?.isLipSyncing,
  });

  const renderAverageScoreBadge = averageScore({ scoresAverage: averageScores, size, headingSize });

  return (
    <>
      <SimpleGrid mb="6" spacingY="4" columns={{ base: 3, sm: 6 }} {...gridProps}>
        {AVERAGE_SCORES_LIST.map(renderAverageScoreBadge)}
      </SimpleGrid>
    </>
  );
}

type TAverageScores = {
  scoresAverage: IRecordedInterviewAverageScoresExtended;
  size: number;
  headingSize: string;
};
const averageScore =
  ({ scoresAverage, size, headingSize }: TAverageScores) =>
  (score: TAverageScoresListItem) => {
    const { key, label } = score;
    const value = scoresAverage[key];
    const withIndicator = key === 'averageAnswerScore';

    return (
      <VStack key={key}>
        <Heading as="h3" fontSize={headingSize} fontWeight="medium">
          {label}
        </Heading>

        <ScoreBadge size={size} value={value} withIndicator={withIndicator} />
      </VStack>
    );
  };
